// How fresh a period's stat lines are. BACKLOG L2, PLAN-2026-09-25 N5.
//
// PURE, AND IN THE DOMAIN, because every client needs the same answer: a
// second frontend that decided for itself whether a 0–0 was a score or a
// wait would decide differently. The server reads the four inputs — the
// instant, the period's games with whether each one's lines have landed, the
// last finished stats run and the last live poll — and hands them here.
//
// THE QUESTION IS PER GAME, NOT PER PERIOD. A Thursday game whose lines have
// landed and a Sunday game still being played are one period; what decides
// whether the board can be read as a score is whether any game that has
// kicked off is still missing its lines. A game that has not kicked off is
// missing nothing yet.
//
// A GAME'S LINES HAVE LANDED when this league's world holds a stat line for
// it. With nflverse the only feed, lines arrive after the game in the nightly
// pull, so a line is a finished game's. TODO(F1): once a live feed writes
// lines while a game is played (BACKLOG L0 decides whether it does), "landed"
// has to mean the official line, not any line.

import { STATS_DELAYED_AFTER_MS, type StatsFreshnessShape } from '../api/clock';

/** One of the period's games, as freshness needs it. */
export interface FreshnessGame {
  /** Kickoff, ISO. */
  start: string;
  /** `games.status`: a canceled or postponed game is no game. */
  status: string;
  /** Whether this league's world holds a stat line for it. */
  landed: boolean;
}

export interface FreshnessInput {
  now: Date;
  /** The period's games, in this league's world. */
  games: readonly FreshnessGame[];
  /** When the last `ok` or `partial` stats run finished, at or before `now`. */
  lastFinal: string | null;
  /**
   * When a live feed last polled successfully. Always null today.
   * TODO(F1): the live feed's last poll (BACKLOG L0, L4).
   */
  lastLivePoll: string | null;
}

const NOT_PLAYED = new Set(['canceled', 'postponed']);

const later = (a: string | null, b: string | null): string | null =>
  a === null ? b : b === null ? a : Date.parse(a) >= Date.parse(b) ? a : b;

/** The period's `stats` field: `none`, `delayed`, `live` or `final`, and since when. */
export function statsFreshness(input: FreshnessInput): StatsFreshnessShape {
  const now = input.now.getTime();
  const started = input.games.filter(
    (g) => !NOT_PLAYED.has(g.status) && Date.parse(g.start) <= now,
  );
  if (started.length === 0) return { state: 'none', asOf: null };
  if (started.every((g) => g.landed)) return { state: 'final', asOf: input.lastFinal };
  const poll = input.lastLivePoll;
  if (poll !== null && now - Date.parse(poll) <= STATS_DELAYED_AFTER_MS) {
    return { state: 'live', asOf: poll };
  }
  return { state: 'delayed', asOf: later(input.lastFinal, poll) };
}
